/**
 * The four lookups a run-detail page needs, each scoped to the two ids the page already knows —
 * deliberately NOT `useActivations()`, which fans out `listSyncsForSource` over every audience in
 * the workspace (see that hook's own header comment): the wrong shape once the caller already has
 * both a `sourceId` and a `syncId`.
 *
 * `listActivationMeta` is the fourth, and carries the same caveats it does on the list: it is one flat
 * read of Lookout's own table, most syncs have no row in it, and it is outside both `isLoading` and
 * `error` — the page must render from ids immediately and must not report a failure of the whole
 * screen because a display name did not resolve.
 */
import { skipToken, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import type { AudienceRecord } from '@/audiences/lib/audience-view';

import { isDeliveryEnabled } from '../lib/activation-list';
import type { DestinationRef, RawSync } from '../types';

export interface SyncDetail {
  sync: RawSync | undefined;
  /** The RudderStack source name for `sourceId`, resolved at render — rows/routes carry ids, never
   * names. */
  audienceName: string | undefined;
  /**
   * The WAREHOUSE behind this audience — `RawSqlAudience.sourceDefinitionName`, e.g. `'postgres'`.
   *
   * Read off the same `source` row the enabled check already resolves, so it costs no extra query.
   * Casing is not guaranteed (the live API returns lowercase `"postgres"`; other rows have shipped
   * capitalized), which is why every consumer resolves it case-insensitively — see `PhaseMark`.
   * `undefined` on older sources that predate the field, and `PhaseMark` falls back to the generic
   * warehouse mark there rather than guessing.
   */
  sourceDefinitionName: string | undefined;
  destination: DestinationRef | undefined;
  /** The far side's own name for the audience this sync writes. `null` = not resolved, never "unnamed". */
  destinationAudienceName: string | null;
  /** The far-side audience id — on the wire for every sync, so it is always a true thing to show. */
  destinationAudienceId: string | undefined;
  /**
   * The LOOKOUT audience this sync ships — `audience_activations.audience_id`, the only id in this
   * hook that addresses `/audiences/:id`. Do not confuse it with the `sourceId` the route carries,
   * which is the rETL source, nor with `destinationAudienceId`, which is Meta's or TikTok's.
   *
   * `null` is ORDINARY, not an error: it means Lookout has no meta row for this sync — a sync built in
   * the RudderStack webapp over a source no Lookout audience owns, or one whose row has not been
   * adopted yet. The backend refuses to invent the link, so a `null` here must render no link rather
   * than a path built from a fallback id. It can also turn non-null on its own: the same adoption pass
   * that resolves a missing far-side name writes `audience_id` too, so a page that opened without a
   * link acquires one when the repair mutation invalidates this read.
   */
  lookoutAudienceId: string | null;
  /**
   * The Lookout audience row this sync ships, for its stored size. `null` whenever
   * {@link lookoutAudienceId} is — a webapp-built sync has no row and never will, so its size is
   * ABSENT rather than unknown, and must render as nothing rather than as "Unknown".
   */
  audienceRecord: AudienceRecord | null;
  /** A record this sync has is in flight. False for a sync that has none — nothing is coming. */
  audienceRecordLoading: boolean;
  /**
   * The audience-record read FAILED, as opposed to there being nothing to read.
   *
   * Kept out of the top-level {@link error} deliberately: that one gates the whole page's error
   * screen, and a size we could not fetch must not blank a page whose subject is the run history.
   * But it cannot be dropped either — without it, a failed lookup and a sync that never had a Lookout
   * audience both arrive as `audienceRecord: null`, and the caption cannot tell "absent" from
   * "unknown", which is the one distinction it exists to make.
   */
  audienceRecordError: Error | null;
  /**
   * Whether this activation is actually delivering — the AND of the connection's, the source's and the
   * destination's own switches. See `ActivationRow.enabled`; this is the same rule for one sync.
   */
  isActive: boolean;
  /** When the name was last checked against the platform; `null` if it never was. */
  destinationAudienceResolvedAt: string | null;
  /**
   * Whether the meta read ANSWERED — not merely whether it stopped.
   *
   * The auto-resolve must not fire off a `null` that is not evidence of absence. Loading produces one;
   * so does an error. Both mean "we do not know whether this sync has a name", and only a successful
   * read distinguishes "no name recorded" from "could not read the record".
   */
  metaSettled: boolean;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useSyncDetail(sourceId: string, syncId: string): SyncDetail {
  const orpc = useOrpc();

  // Each query is enabled on the id it actually needs to be useful: the source's syncs need a
  // `sourceId`; the audience name is only worth resolving once we have one; the destination is
  // only worth resolving once there's a `syncId` to eventually match a `destinationId` against.
  const syncsQuery = useQuery({
    ...orpc.activations.listSyncsForSource.queryOptions({ input: { sourceId } }),
    enabled: Boolean(sourceId),
  });
  const audiencesQuery = useQuery({
    ...orpc.activations.listSqlAudiences.queryOptions({ input: {} }),
    enabled: Boolean(sourceId),
  });
  const destinationsQuery = useQuery({
    ...orpc.activations.listDestinations.queryOptions({ input: {} }),
    enabled: Boolean(syncId),
  });
  const metaQuery = useQuery({
    ...orpc.activations.listActivationMeta.queryOptions({ input: {} }),
    enabled: Boolean(syncId),
  });

  const sync = useMemo(
    () => syncsQuery.data?.syncs.find((candidate) => candidate.id === syncId),
    [syncsQuery.data?.syncs, syncId],
  );
  const audienceName = useMemo(
    () => audiencesQuery.data?.sources.find((source) => source.id === sourceId)?.name,
    [audiencesQuery.data?.sources, sourceId],
  );
  const destination = useMemo(
    () => destinationsQuery.data?.destinations.find((candidate) => candidate.id === sync?.destinationId),
    [destinationsQuery.data?.destinations, sync?.destinationId],
  );

  // Keyed by `connectionId`, which IS the sync id. A miss is ordinary, not exceptional: only syncs
  // Lookout itself minted are recorded.
  const meta = useMemo(
    () => metaQuery.data?.activations.find((candidate) => candidate.connectionId === syncId),
    [metaQuery.data?.activations, syncId],
  );

  // ANSWERED, and an error is not an answer.
  //
  // Not `!isLoading`: a disabled query is neither loading nor settled, and treating it as settled would
  // fire the auto-resolve before the page even has a `syncId`. And not `isSuccess || isError`, which is
  // what this was: the rule is "do not act on a `null` that is not evidence of absence", and an errored
  // read produces exactly such a `null` — the same one loading does, from the same position of not
  // knowing. `destination_audience_name` is deliberately nullable so a failed or not-yet-run resolve
  // stays distinguishable from an audience the platform genuinely calls nothing; treating `isError` as
  // settled collapses that distinction the schema takes care to keep.
  //
  // What it cost: a failed read of Lookout's OWN table fired a resolve against Meta/Iterable, which
  // then WRITES `destination_audience_name` back — mutating persisted state on the strength of a read
  // that failed, for a row that may already hold a perfectly good name. It could not even be expected
  // to succeed: the resolve needs `rs_account_id` and `platform_account_id` to know which account to
  // ask, and those live in the row we just failed to read.
  //
  // The cost of this direction is one skipped repair attempt on an errored page open. The next open
  // retries, and the repair is best-effort by design.
  const metaSettled = metaQuery.isSuccess;

  /**
   * The Lookout audience row behind this sync, for its stored size. A FIFTH query, and deliberately a
   * sequential one: the only id that addresses `/audiences/:id` is `meta.audienceId`, so this cannot
   * start until `metaQuery` has come back. The size therefore arrives after the rest of the header —
   * it is a late addition to a rendered strip, never something the strip waits on.
   *
   * `skipToken` rather than `enabled`, so a sync with no Lookout audience issues no request at all.
   * That is the ORDINARY case for a webapp-built sync (see `lookoutAudienceId`), and there is no fact
   * to fetch for one — which is why the caller renders nothing rather than "Unknown" for it.
   */
  const audienceRecordQuery = useQuery(
    orpc.audience.get.queryOptions({ input: meta?.audienceId ? { id: meta.audienceId } : skipToken }),
  );

  const source = audiencesQuery.data?.sources.find((candidate) => candidate.id === sourceId);
  // A bit that has not arrived yet reads as ON: the platform's columns default to `true`, so only an
  // explicit `false` is a disable and an unresolved lookup must not manufacture one.
  // The same rule the list climbs — one function, so the two cannot drift again (they already did
  // once, and a sync disabled at its SOURCE shipped as active).
  const isActive = isDeliveryEnabled({
    sync: sync?.enabled,
    source: source?.enabled,
    destination: destination?.enabled,
  });

  return {
    sync,
    audienceName,
    // Off `source`, already resolved above for `isActive` — no new query, no new lookup.
    sourceDefinitionName: source?.sourceDefinitionName,
    destination,
    isActive,
    destinationAudienceName: meta?.destinationAudienceName ?? null,
    // The sync's own config first: it is present whether or not Lookout recorded the activation.
    destinationAudienceId: sync?.config?.destination?.audienceId ?? meta?.destinationAudienceId,
    // No fallback ladder, unlike every other field here — deliberately. The other two rungs available
    // (`sourceId`, the far-side id) are ids of DIFFERENT objects, so a fallback would not degrade the
    // link, it would point it somewhere wrong. Either Lookout recorded which audience this is, or it
    // did not.
    lookoutAudienceId: meta?.audienceId ?? null,
    audienceRecord: audienceRecordQuery.data?.audience ?? null,
    /** True only while a record this sync actually HAS is in flight — never for a sync with no audience. */
    audienceRecordLoading: audienceRecordQuery.isLoading,
    audienceRecordError: audienceRecordQuery.error,
    destinationAudienceResolvedAt: meta?.resolvedAt ?? null,
    metaSettled,
    // `metaQuery` is deliberately in neither of the next two: the page renders from ids and the name
    // upgrades in place, and a missing display name is not a failure of this screen.
    isLoading: syncsQuery.isLoading || audiencesQuery.isLoading || destinationsQuery.isLoading,
    error: syncsQuery.error ?? audiencesQuery.error ?? destinationsQuery.error,
    refetch: () => {
      void syncsQuery.refetch();
      void audiencesQuery.refetch();
      void destinationsQuery.refetch();
      void metaQuery.refetch();
      // The audience record is refetched with the rest. It is a `skipToken` query for a sync with no
      // Lookout audience, where this is a no-op — but for one that HAS an audience it is the only way
      // a failed size lookup ever recovers, and leaving it out made the page's Retry button unable to
      // fix the one thing it looked like it should.
      void audienceRecordQuery.refetch();
    },
  };
}
